import { useEffect, useMemo, useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Badge,
  type BadgeTone,
  Banner,
  Button,
  Card,
  CardBody,
  EmptyState,
  Input,
} from "@jotty/ui";
import { ApiError, meetings, type Meeting } from "~/lib/api";
import { useAuthStore } from "~/lib/auth-store";

export const Route = createFileRoute("/_authed/meetings/")({
  component: MeetingsPage,
});

// ---------------------------------------------------------------------------
// Status chip config — covers all statuses including PRO-21 additions
// ---------------------------------------------------------------------------

type FilterChip = "all" | "this_week" | "failed" | "mine";

const statusTone: Record<string, BadgeTone> = {
  scheduled: "neutral",
  in_progress: "warning",
  completed: "success",
  summarized: "success",
  recording: "warning",
  no_transcript: "neutral",
  too_short: "neutral",
  summary_failed: "neutral",
  failed: "danger",
  archived: "neutral",
};

function prettyStatus(s: string): string {
  return s.replace(/_/g, " ");
}

// ---------------------------------------------------------------------------
// Day grouping helpers
// ---------------------------------------------------------------------------

function dayLabel(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  if (d.getTime() === today.getTime()) return "TODAY";
  if (d.getTime() === yesterday.getTime()) return "YESTERDAY";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

function groupByDay(items: Meeting[]): { label: string; meetings: Meeting[] }[] {
  const groups: Map<string, Meeting[]> = new Map();
  for (const m of items) {
    const key = dayLabel(m.started_at ?? m.created_at);
    if (!groups.has(key)) groups.set(key, []);
    groups.get(key)!.push(m);
  }
  return Array.from(groups.entries()).map(([label, meetings]) => ({ label, meetings }));
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isThisWeek(dateStr: string): boolean {
  const date = new Date(dateStr);
  const now = new Date();
  const weekStart = new Date(now);
  weekStart.setDate(now.getDate() - now.getDay());
  weekStart.setHours(0, 0, 0, 0);
  return date >= weekStart;
}

function formatDuration(startedAt?: string | null, endedAt?: string | null): string {
  if (!startedAt || !endedAt) return "";
  const ms = new Date(endedAt).getTime() - new Date(startedAt).getTime();
  const mins = Math.round(ms / 60000);
  if (mins < 1) return "";
  if (mins < 60) return `${mins}m`;
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

function MeetingsPage() {
  const tenants = useAuthStore((s) => s.tenants);
  const user = useAuthStore((s) => s.user);
  const activeTenant = tenants[0];

  const [items, setItems] = useState<Meeting[] | null>(null);
  const [activeChip, setActiveChip] = useState<FilterChip>("all");
  const [query, setQuery] = useState("");
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activeTenant) return;
    let cancelled = false;
    setError(null);
    setItems(null);
    (async () => {
      try {
        const res = await meetings.list(activeTenant.id);
        if (!cancelled) setItems(res.meetings ?? []);
      } catch (err) {
        if (!cancelled)
          setError(err instanceof ApiError ? err.message : "Could not load meetings");
      }
    })();
    return () => { cancelled = true; };
  }, [activeTenant?.id]);

  const failedCount = useMemo(
    () => items?.filter((m) => m.status === "failed").length ?? 0,
    [items],
  );

  const filtered = useMemo(() => {
    if (!items) return null;
    let result = items;

    if (activeChip === "this_week") {
      result = result.filter((m) => isThisWeek(m.started_at ?? m.created_at));
    } else if (activeChip === "failed") {
      result = result.filter((m) => m.status === "failed");
    } else if (activeChip === "mine" && user?.email) {
      result = result.filter((m) => m.attendees.includes(user.email!));
    }

    const q = query.trim().toLowerCase();
    if (q) {
      result = result.filter(
        (m) =>
          m.title.toLowerCase().includes(q) ||
          m.attendees.some((a) => a.toLowerCase().includes(q)),
      );
    }

    return result;
  }, [items, activeChip, query, user?.email]);

  if (!activeTenant) return null;

  const grouped = filtered ? groupByDay(filtered) : [];

  return (
    <div className="space-y-6">
      <header className="flex items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight text-[color:var(--color-text)]">
            Meetings
          </h1>
          <p className="mt-1 text-sm text-[color:var(--color-text-muted)]">
            Every meeting in <span className="font-medium">{activeTenant.name}</span>.
          </p>
        </div>
        <Link to="/meetings/new">
          <Button variant="primary">New meeting</Button>
        </Link>
      </header>

      {/* Filter chips + search */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex gap-2">
          {(["all", "this_week", "failed", "mine"] as FilterChip[]).map((chip) => (
            <button
              key={chip}
              onClick={() => setActiveChip(chip)}
              className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                activeChip === chip
                  ? "bg-[color:var(--color-primary)] text-white"
                  : "border border-[color:var(--color-border)] bg-[color:var(--color-surface)] text-[color:var(--color-text-muted)] hover:text-[color:var(--color-text)]"
              }`}
            >
              {chip === "all" && "All"}
              {chip === "this_week" && "This week"}
              {chip === "failed" && `Failed${failedCount > 0 ? ` (${failedCount})` : ""}`}
              {chip === "mine" && "Mine only"}
            </button>
          ))}
        </div>
        <div className="flex-1 min-w-[200px]">
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search by title or attendee…"
          />
        </div>
      </div>

      {error ? <Banner tone="danger">{error}</Banner> : null}

      {filtered === null && !error ? (
        <p className="text-sm text-[color:var(--color-text-muted)]">Loading…</p>
      ) : grouped.length === 0 ? (
        <EmptyState
          title="No meetings yet"
          description="Schedule your first one."
          action={
            <Link to="/meetings/new">
              <Button>New meeting</Button>
            </Link>
          }
        />
      ) : (
        <div className="space-y-6">
          {grouped.map(({ label, meetings: group }) => (
            <div key={label}>
              <p className="mb-2 text-xs font-semibold tracking-widest text-[color:var(--color-text-muted)]">
                {label}
              </p>
              <ul className="grid gap-2">
                {group.map((m) => (
                  <li key={m.id}>
                    <Link to="/meetings/$id" params={{ id: m.id }} className="block">
                      <Card className="transition-colors hover:border-[color:var(--color-border-strong)]">
                        <CardBody className="flex items-center justify-between gap-4">
                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2">
                              <h3 className="truncate font-medium text-[color:var(--color-text)]">
                                {m.title}
                              </h3>
                              <Badge tone={(statusTone[m.status] ?? "neutral") as BadgeTone}>
                                {prettyStatus(m.status)}
                              </Badge>
                            </div>
                            <p className="mt-1 text-xs text-[color:var(--color-text-muted)]">
                              {m.attendees.length === 0
                                ? "No attendees"
                                : `${m.attendees.length} attendee${m.attendees.length === 1 ? "" : "s"}`}
                              {" · "}
                              {m.started_at
                                ? new Date(m.started_at).toLocaleTimeString([], {
                                    hour: "2-digit",
                                    minute: "2-digit",
                                  })
                                : `created ${new Date(m.created_at).toLocaleDateString()}`}
                              {formatDuration(m.started_at, m.ended_at)
                                ? ` · ${formatDuration(m.started_at, m.ended_at)}`
                                : ""}
                            </p>
                          </div>
                          <DeliveryBadges meeting={m} />
                        </CardBody>
                      </Card>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delivery channel badges — one per channel that's actually been attempted,
// colored by its latest status (from GET /tenants/{id}/meetings receipts).
// ---------------------------------------------------------------------------

const CHANNEL_LABELS: Record<string, string> = {
  email: "Email",
  slack: "Slack",
  google_docs: "Docs",
};

const DELIVERY_TONE: Record<string, string> = {
  ok: "bg-green-50 text-green-700",
  skipped: "bg-[color:var(--kugie-neutral-100)] text-[color:var(--color-text-subtle)]",
  failed: "bg-red-50 text-red-700",
};

function DeliveryBadges({ meeting }: { meeting: Meeting }) {
  if (!meeting.deliveries?.length) return null;

  const latestByChannel = new Map<string, (typeof meeting.deliveries)[number]>();
  for (const r of meeting.deliveries) {
    if (!latestByChannel.has(r.channel)) latestByChannel.set(r.channel, r);
  }

  return (
    <div className="flex gap-1.5 flex-shrink-0">
      {[...latestByChannel.values()].map((r) => (
        <span
          key={r.channel}
          title={r.error_reason || undefined}
          className={`rounded-full px-2 py-0.5 text-[10px] font-medium ${DELIVERY_TONE[r.status] ?? DELIVERY_TONE.skipped}`}
        >
          {CHANNEL_LABELS[r.channel] ?? r.channel}
        </span>
      ))}
    </div>
  );
}